import {H2} from '@jetbrains/ring-ui/components/heading/heading'
import Link from '@jetbrains/ring-ui/components/link/link'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import React from 'react'

import {resolveHelpURL} from '../../../utils/url'
import HelpLink from '../../common/HelpLink/HelpLink'
import IconButton from '../../common/IconButton/IconButton'

import CertificateInfo from './CertificateInfo/CertificateInfo'
import CertificateInstallationForm from './CertificateInstallationForm/CertificateInstallationForm'
import httpsConfigurationApi from './HttpsConfigurationPage.api'
import {CertificateInstallationTabs} from './HttpsConfigurationPage.types'

import styles from './HttpsConfigurationPage.css'

import {restApi} from 'src/services/rest'

type Props = {
  defaultTab?: CertificateInstallationTabs
}

function HttpsConfigurationPage({defaultTab = CertificateInstallationTabs.FETCH}: Props) {
  const {certificate, isCertificateLoading} =
    httpsConfigurationApi.endpoints.getCertificate.useQuery(undefined, {
      refetchOnFocus: false,
      selectFromResult: ({isFetching, data, isUninitialized}) => ({
        isCertificateLoading: isFetching || isUninitialized,
        certificate: data,
      }),
    })
  const certificateExists = certificate != null

  const {data: nodes, isLoading: isNodesInfoLoading} = restApi.endpoints.nodes.useQuery(
    {fields: 'count'},
    {skip: isCertificateLoading || certificateExists},
  )
  const isMultiNodeSetup = nodes?.count != null && nodes.count > 1

  const {pendingAcmeOrder, isOrderLoading} =
    httpsConfigurationApi.endpoints.getPendingAcmeOrder.useQuery(undefined, {
      selectFromResult: ({isLoading, data}) => ({
        isOrderLoading: isLoading,
        pendingAcmeOrder: data,
      }),
      skip: isCertificateLoading || isNodesInfoLoading || certificateExists || isMultiNodeSetup,
    })

  if (isCertificateLoading || isOrderLoading || isNodesInfoLoading) {
    return <LoaderInline>{'Loading...'}</LoaderInline>
  }

  return (
    <>
      {!certificateExists && (
        <>
          <div className={styles.warning}>
            {'These settings affect the built-in Tomcat server configuration.'}
            <br />
            {'If your TeamCity server is '}
            <HelpLink
              url={resolveHelpURL(
                'configuring-proxy-server.html#Set+Up+TeamCity+Server+Behind+Proxy',
              )}
              Component={Link}
              preservePlus
            >
              {'behind a proxy'}
            </HelpLink>
            {', configure HTTPS on '}
            <HelpLink
              url={resolveHelpURL('how-to.html#Configure+HTTPS+for+TeamCity+Web+UI')}
              Component={Link}
              preservePlus
            >
              {'the proxy side'}
            </HelpLink>
            {'.'}
            <br />
            {'Enabling the settings on this page '}
            <strong>{'may break your existing proxy configuration.'}</strong>
          </div>
          <div className={styles.description}>
            {
              "To enable HTTPS on the server, install an SSL certificate manually or fetch it from Let's Encrypt."
            }
            <br />
            {'TeamCity supports .pem certificates and RSA keys of the PKCS#1 or PKCS#8 format.'}
            <HelpLink
              url={resolveHelpURL('https-server-settings.html')}
              Component={IconButton}
              icon={'help'}
            />
          </div>
        </>
      )}
      <H2 className={styles.title}>{`Certificate${certificateExists ? ' Installed' : ''}`}</H2>
      {certificateExists ? (
        <CertificateInfo {...certificate} />
      ) : (
        <CertificateInstallationForm
          isMultiNodeSetup={isMultiNodeSetup}
          defaultTab={
            isMultiNodeSetup
              ? CertificateInstallationTabs.UPLOAD
              : pendingAcmeOrder != null
              ? CertificateInstallationTabs.FETCH
              : defaultTab
          }
        />
      )}
    </>
  )
}

export default HttpsConfigurationPage
